import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Globe, Clock, ArrowUpRight, Facebook, Instagram, Linkedin } from "lucide-react";
import { NAV, COMPANY } from "../lib/content";

const SOCIALS = [
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61589380053879&mibextid=wwXIfr", Icon: Facebook },
  { label: "Instagram", href: "https://www.instagram.com/nexthorizongroups", Icon: Instagram },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/next-horizon-builders-inc/", Icon: Linkedin },
];

export const Footer = () => (
  <footer className="relative bg-navy text-white grain overflow-hidden" data-testid="site-footer">
    <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-12">
      {/* CTA band */}
      <div className="border-b border-white/10 pb-16 md:pb-20 mb-16 grid md:grid-cols-2 gap-10 items-end">
        <div>
          <p className="eyebrow mb-6">Let's Build Together</p>
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Ready to break<br />ground on what's next?
          </h2>
        </div>
        <div className="md:justify-self-end">
          <Link
            to="/contact"
            data-testid="footer-contact-cta"
            className="group inline-flex items-center gap-4 border border-white/30 px-9 py-5 text-xs font-semibold uppercase tracking-[0.22em] hover:bg-gold hover:border-gold transition-all duration-300"
          >
            Start a Conversation
            <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
          </Link>
        </div>
      </div>

      {/* Columns */}
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <img src="/media/logo-white.png" alt="Next Horizon Builders" className="h-[120px] w-auto mb-5" />
          <p className="font-serif font-light text-2xl md:text-3xl tracking-tight text-white mb-5">
            Next Horizon Builders
          </p>
          <p className="text-white/60 max-w-sm leading-relaxed text-sm">
            {COMPANY.subtitle}
          </p>
          <p className="mt-6 text-white/40 text-xs tracking-wider uppercase">{COMPANY.license}</p>
        </div>

        <div className="md:col-span-3">
          <p className="eyebrow mb-6">Explore</p>
          <ul className="space-y-3">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="text-white/70 hover:text-gold transition-colors duration-300 text-sm">
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="eyebrow mb-6">Contact</p>
          <ul className="space-y-4 text-sm text-white/70">
            <li className="flex items-start gap-3">
              <MapPin size={18} className="text-gold mt-0.5 shrink-0" />
              <span>{COMPANY.address}</span>
            </li>
            <li className="flex items-center gap-3">
              <Phone size={18} className="text-gold shrink-0" />
              <a href={COMPANY.phoneHref} className="hover:text-gold transition-colors">{COMPANY.phone}</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={18} className="text-gold shrink-0" />
              <a href={`mailto:${COMPANY.email}`} className="hover:text-gold transition-colors">{COMPANY.email}</a>
            </li>
            <li className="flex items-center gap-3">
              <Globe size={18} className="text-gold shrink-0" />
              <a href={COMPANY.website} target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">{COMPANY.websiteLabel}</a>
            </li>
            <li className="flex items-start gap-3">
              <Clock size={18} className="text-gold mt-0.5 shrink-0" />
              <span>
                {COMPANY.hours.map((h) => (
                  <span key={h.days} className="block">{h.days} · {h.time}</span>
                ))}
              </span>
            </li>
          </ul>

          <p className="eyebrow mt-10 mb-5">Follow Us</p>
          <div className="flex items-center gap-3" data-testid="footer-socials">
            {SOCIALS.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="inline-flex items-center justify-center h-10 w-10 border border-white/20 text-white/70 hover:text-gold hover:border-gold transition-colors duration-300"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/40">
        <span>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</span>
        <span>{COMPANY.region}</span>
      </div>
    </div>
  </footer>
);
